/*
 * Buy panel: quantity drives a reward track, and the track drives the prices.
 *
 * The percentages here are display only — the real discount is applied by the
 * cart rules, so the tier settings in the theme editor must match them.
 */
(() => {
  // Included by both the pack selector and the plan rows
  if (window.buyPanelLoaded) return;
  window.buyPanelLoaded = true;

  const money = (cents) => {
    const value = (cents / 100).toFixed(2);
    return `$${value.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  class BuyPanel {
    /*
     * Scoped to the product form, not to the plan rows: the pack selector, the
     * price and the reward bar are all separate blocks, and a product with no
     * subscriptions has no plan rows at all — the packs still have to work.
     */
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('input[name="quantity"]');
      this.collect();
      this.readUnit();

      // First paint matches what Liquid rendered, so nothing transitions in
      this.first = true;
      form.addEventListener('change', () => this.render());
      form.addEventListener('input', () => this.render());

      // Clicking a tier sets the quantity, which drives everything else
      this.form.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-tier-quantity]');
        if (!chip || !this.input) return;
        event.preventDefault();
        this.setQuantity(parseInt(chip.dataset.tierQuantity, 10) || 1);
      });

      // The stepper fires its own events, which the form listener may miss
      if (this.input) {
        ['input', 'change'].forEach((type) =>
          this.input.addEventListener(type, () => this.render())
        );
      }

      // Adding to cart moves the real cart total, so the bar behind the
      // projection is stale until it is re-rendered from the server
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshProgress());
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data && data.html) this.applyVariant(data.html);
        });
      }

      this.render();
    }

    /*
     * Only the plan rows live inside this panel — the price, the stepper, the
     * pack selector and the reward bar are sibling blocks, so they are looked
     * up in the product form instead.
     */
    collect() {
      this.panel = this.form.querySelector('[data-buy-panel]');
      const root = this.panel || this.form;
      this.priceBlock = this.form.querySelector('[data-product-price]');
      this.progress = this.form.querySelector('[data-progress-projection]');
      this.track = this.form.querySelector('[data-buy-panel-track]');
      this.fill = this.form.querySelector('[data-buy-panel-fill]');
      this.tiers = [...this.form.querySelectorAll('[data-tier-quantity]')].map((el) => ({
        el,
        quantity: parseInt(el.dataset.tierQuantity, 10) || 1,
        percent: parseInt(el.dataset.tierPercent, 10) || 0,
      }));
      this.planInputs = [...root.querySelectorAll('[data-buy-panel-plan]')];
      this.planPrices = [...root.querySelectorAll('[data-buy-panel-plan-price]')];
      this.frequency = root.querySelector('[data-buy-panel-frequency]');
    }

    /* The panel carries the unit price; without one, the price block does */
    readUnit() {
      this.unit =
        parseInt(this.panel?.dataset.unitPrice, 10) ||
        parseInt(this.priceBlock?.dataset.unitPrice, 10) ||
        0;
    }

    /*
     * product-info swaps a fixed list of nodes on variant change and this panel
     * is not one of them, so its unit price and each plan's allocation would go
     * on describing whichever variant rendered first. The event carries the
     * fetched document — take the fresh markup from it, then put the shopper's
     * plan and frequency back.
     */
    applyVariant(html) {
      const plan = this.planInputs.find((i) => i.checked)?.value ?? null;
      const frequency = this.frequency?.value ?? null;

      if (this.panel) {
        const fresh =
          (this.panel.id && html.getElementById(this.panel.id)) ||
          html.querySelector('[data-buy-panel]');
        if (fresh) {
          this.panel.innerHTML = fresh.innerHTML;
          if (fresh.dataset.unitPrice) this.panel.dataset.unitPrice = fresh.dataset.unitPrice;
        }
      }

      this.collect();

      /*
       * product-info copies the price block's innerHTML but not the element
       * itself, so the compare-at and unit figures on its root would keep
       * describing the previous variant and the struck price would be wrong.
       */
      if (this.priceBlock && this.priceBlock.id) {
        const freshPrice = html.getElementById(this.priceBlock.id);
        if (freshPrice) {
          ['unitPrice', 'unitCompare'].forEach((key) => {
            if (freshPrice.dataset[key] !== undefined) {
              this.priceBlock.dataset[key] = freshPrice.dataset[key];
            }
          });
        }
      }

      this.readUnit();

      const restored = this.planInputs.find((i) => i.value === plan);
      if (restored) restored.checked = true;
      if (frequency && this.frequency) {
        const match = [...this.frequency.options].find((o) => o.value === frequency);
        if (match) this.frequency.value = frequency;
      }

      this.render();
    }

    /*
     * The cart drawer renders the same snippet, so pull its freshly rendered
     * bar rather than recomputing thresholds, notches and copy in JS.
     */
    async refreshProgress() {
      const bar = this.progress && this.progress.closest('.cart-progress');
      if (!bar) return;

      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      let fresh;
      try {
        const response = await fetch(`${cartUrl}?section_id=cart-drawer`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        fresh = doc.querySelector('.cart-progress');
      } catch (error) {
        return;
      }
      if (!fresh) return;

      bar.dataset.cartTotal = fresh.dataset.cartTotal || '0';
      bar.dataset.maxThreshold = fresh.dataset.maxThreshold || '0';
      bar.innerHTML = fresh.innerHTML;

      // The drawer's copy carries no projection, so put ours back and re-point
      const track = bar.querySelector('.cart-progress__track');
      this.progress = null;
      if (track) {
        const projection = document.createElement('div');
        projection.className = 'cart-progress__projection';
        projection.setAttribute('data-progress-projection', '');
        track.prepend(projection);
        this.progress = projection;
      }

      this.render();
    }

    setQuantity(value) {
      if (!this.input) return;
      this.input.value = String(Math.max(1, value));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.render();
    }

    get quantity() {
      return Math.max(1, parseInt(this.input?.value, 10) || 1);
    }

    // Highest tier the current quantity has reached
    get tier() {
      return this.tiers.reduce(
        (best, t) => (this.quantity >= t.quantity && t.quantity >= best.quantity ? t : best),
        { quantity: 1, percent: 0 }
      );
    }

    get nextTier() {
      return this.tiers
        .filter((t) => t.quantity > this.quantity)
        .sort((a, b) => a.quantity - b.quantity)[0];
    }

    unitAfterTier(base) {
      return Math.round(base * (1 - this.tier.percent / 100));
    }

    render() {
      const qty = this.quantity;
      const selected = this.planInputs.find((i) => i.checked);
      const label = selected?.closest('label');
      // Intervals can price differently, so the chosen one wins over the row
      const chosenOption = label
        ?.querySelector('[data-buy-panel-frequency]')
        ?.selectedOptions?.[0];
      const selectedUnit =
        parseInt(
          chosenOption?.dataset.planUnit ||
            label?.querySelector('[data-plan-unit]')?.dataset.planUnit,
          10
        ) || this.unit;

      // The price block is the running total for the order.
      // Careful: inside .price__sale, .price-item--regular is the compare-at
      // price, so target the two roles separately rather than by class alone.
      if (this.priceBlock) {
        const unitCompare = parseInt(this.priceBlock.dataset.unitCompare, 10) || 0;
        const orderTotal = this.unitAfterTier(selectedUnit) * qty;

        const payable =
          this.priceBlock.querySelector('.price__sale .price-item--sale') ||
          this.priceBlock.querySelector('.price__regular .price-item--regular');
        if (payable) payable.textContent = money(orderTotal);

        // The struck price scales with quantity too, or it stops comparing
        const compare = this.priceBlock.querySelector('.price__sale .price-item--regular');
        if (compare && unitCompare > 0) compare.textContent = money(unitCompare * qty);
      }

      // Each plan row prices the whole order — its own rate at the current
      // tier, times the chosen pack quantity
      this.planPrices.forEach((el) => {
        const base = parseInt(el.dataset.planUnit, 10) || this.unit;
        el.textContent = money(this.unitAfterTier(base) * qty);
      });

      // The fill reaches the highest tier the quantity has met
      const reached = this.tiers.reduce((n, t, i) => (qty >= t.quantity ? i : n), 0);
      if (this.first && this.track) {
        this.track.dataset.ready = 'false';
        requestAnimationFrame(() => {
          if (this.track) this.track.dataset.ready = 'true';
        });
        this.first = false;
      }
      const count = this.tiers.length || 1;
      if (this.fill) {
        this.fill.style.width = `calc((100% - 8px) * ${(reached + 1) / count})`;
      }
      this.tiers.forEach((t, i) => {
        t.el.dataset.covered = String(i <= reached);
        t.el.setAttribute('aria-pressed', String(qty === t.quantity));
        // Per-unit price follows the chosen plan, not just the one-time price
        const each = t.el.querySelector('[data-segment-each]');
        if (each) {
          if (!each.dataset.template) each.dataset.template = each.textContent.trim();
          const value = Math.round(selectedUnit * (1 - t.percent / 100));
          each.textContent = each.dataset.template.replace(/\$[\d.,]+/, money(value));
        }
      });


      // Show where the reward bar would land with this order added
      if (this.progress) {
        const bar = this.progress.closest('.cart-progress');
        const cartTotal = parseInt(bar?.dataset.cartTotal, 10) || 0;
        const max = parseInt(bar?.dataset.maxThreshold, 10) || 0;
        const projected = cartTotal + this.unitAfterTier(selectedUnit) * qty;
        this.progress.style.width = max
          ? `${Math.min(100, (projected / max) * 100)}%`
          : '0%';
      }

      // The product form posts this; empty means one-time
      const field = document.querySelector('input[name="selling_plan"]');
      if (field && selected) {
        field.value = this.frequency && selected.value ? this.frequency.value : selected.value;
      }
    }
  }

  /*
   * The pack selector loads this script too, since the plan rows disappear on a
   * product with no subscriptions and the packs still need driving.
   */
  const boot = () => {
    const scopes = new Set();
    document.querySelectorAll('[data-buy-panel], [data-buy-panel-track]').forEach((el) => {
      scopes.add(el.closest('product-info, .product') || document.body);
    });
    scopes.forEach((scope) => {
      if (scope.dataset.buyPanelReady) return;
      scope.dataset.buyPanelReady = 'true';
      new BuyPanel(scope);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The section re-renders on variant change
  new MutationObserver(boot).observe(document.body, { childList: true, subtree: true });
})();
